/**
 * Application Dashboard page.
 * Bento grid layout with warm gradient cards and summary stats.
 */

import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useProjects } from "../hooks/useProjects";
import { useWorkspace } from "../contexts/WorkspaceContext";
import { useAuth } from "../lib/AuthContext";
import { useWorkspaceUrl } from "../lib/routes";
import {
  FolderKanban,
  FileText,
  Clock,
  Plus,
  Pin,
  Users,
  Loader2,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";
import { canSeeIndexStatus, useVertexIndexStatus } from "../hooks/useVertexIndexStatus";
import { useMyWorkspaceRole } from "../hooks/useWorkspaceMembers";

export function AppDashboard() {
  const { t } = useTranslation();
  const { activeWorkspace } = useWorkspace();
  const { user } = useAuth();
  const { data: projectsData, isLoading: projectsLoading } = useProjects();
  const urls = useWorkspaceUrl();

  const projects = projectsData?.projects || [];
  const pinnedProjects = projects.filter((p) => p.pinned);
  const recentProjects = projects.slice(0, 5);

  const totalDocuments = projects.reduce(
    (sum, p) => sum + (p.document_count || 0),
    0
  );
  const activeProjectCount = projects.length;
  // Same UI, feature off: a role that may not read the index status gets no indexing card.
  const showIndexing = canSeeIndexStatus(useMyWorkspaceRole());
  const { data: vertexStatus, isLoading: vertexLoading, isError: vertexError, reindexFailed } = useVertexIndexStatus(showIndexing);

  const displayName =
    user?.displayName || user?.email?.split("@")[0] || t("dashboard.title");

  return (
    <div>
      {!activeWorkspace ? (
        <div className="bento-card bento-surface p-8 text-center">
          <FolderKanban className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-lg font-medium text-foreground mb-2">
            {t("dashboard.noWorkspace")}
          </h2>
          <p className="text-muted-foreground mb-4">
            {t("dashboard.noWorkspaceMessage")}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {/* ── 1. Hero Card (warm gradient, spans 2) ── */}
          <div className="bento-card bento-warm lg:col-span-2 md:col-span-2 flex flex-col justify-between min-h-[220px]">
            <div>
              <p className="text-sm font-medium bento-muted">
                {activeWorkspace.name}
              </p>
              <h1 className="text-3xl sm:text-4xl font-bold mt-2">
                Welcome back, {displayName}
              </h1>
            </div>
            {urls && (
              <div className="flex gap-3 mt-6">
                <Link to={urls.projects} className="kii-btn kii-btn-soft kii-btn-sm">
                  <FolderKanban className="h-4 w-4" />
                  {t("dashboard.projects")}
                </Link>
                <Link
                  to={`${urls.projects}?action=new`}
                  className="kii-btn kii-btn-soft kii-btn-sm"
                >
                  <Plus className="h-4 w-4" />
                  {t("dashboard.newProject")}
                </Link>
              </div>
            )}
          </div>

          {/* ── 2. Team Card (mist) ── */}
          <div className="bento-card bento-mist flex flex-col justify-between min-h-[220px]">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium bento-muted">
                {t("dashboard.team")}
              </span>
              <Users className="h-5 w-5 bento-muted" />
            </div>
            <div>
              <span className="text-5xl font-bold leading-none">&mdash;</span>
              <p className="text-sm bento-muted mt-2">
                {t("dashboard.manageMembers")}
              </p>
            </div>
          </div>

          {/* ── 3. Projects Card (sage, interactive) ── */}
          {urls && (
            <Link
              to={urls.projects}
              className="bento-card bento-sage bento-interactive flex flex-col justify-between min-h-[160px]"
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium bento-muted">
                  {t("dashboard.projects")}
                </span>
                <FolderKanban className="h-5 w-5 bento-muted" />
              </div>
              <div>
                {projectsLoading ? (
                  <Loader2 className="h-10 w-10 animate-spin bento-muted" />
                ) : (
                  <>
                    <span className="text-5xl font-bold leading-none">
                      {activeProjectCount}
                    </span>
                    <p className="text-sm bento-muted mt-2">
                      {t("dashboard.projectCount", {
                        count: activeProjectCount,
                      })}
                    </p>
                  </>
                )}
              </div>
            </Link>
          )}

          {/* ── 4. Documents Card (gold, interactive) ── */}
          {urls && (
            <Link
              to={urls.projects}
              className="bento-card bento-gold bento-interactive flex flex-col justify-between min-h-[160px]"
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium bento-muted">
                  Documents
                </span>
                <FileText className="h-5 w-5 bento-muted" />
              </div>
              <div>
                {projectsLoading ? (
                  <Loader2 className="h-10 w-10 animate-spin bento-muted" />
                ) : (
                  <>
                    <span className="text-5xl font-bold leading-none">
                      {totalDocuments}
                    </span>
                    <p className="text-sm bento-muted mt-2">
                      {t("dashboard.docs", { count: totalDocuments })}
                    </p>
                  </>
                )}
              </div>
            </Link>
          )}

          {/* ── 5. Vertex Index Status Card (forest) ── */}
          {showIndexing && (
            <div className="bento-card bento-forest flex flex-col justify-between min-h-[160px]">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium bento-muted">
                  Indexing
                </span>
                {vertexLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin bento-muted" />
                ) : vertexError && !vertexStatus ? (
                  <AlertTriangle className="h-5 w-5 text-red-400" />
                ) : vertexStatus && vertexStatus.total_pending > 0 ? (
                  <Loader2 className="h-5 w-5 animate-spin bento-muted" />
                ) : (
                  <CheckCircle2 className="h-5 w-5 bento-muted" />
                )}
              </div>
              <div>
                {vertexLoading ? (
                  <Loader2 className="h-8 w-8 animate-spin bento-muted" />
                ) : vertexError && !vertexStatus ? (
                  <p className="text-sm text-red-400 flex items-center gap-1">
                    <AlertTriangle className="h-4 w-4 shrink-0" />
                    {t("dashboard.indexing.loadFailed")}
                  </p>
                ) : vertexStatus && vertexStatus.total_pending > 0 ? (
                  <>
                    <span className="text-5xl font-bold leading-none">
                      {vertexStatus.total_pending}
                    </span>
                    <p className="text-sm bento-muted mt-2">
                      pending indexing
                    </p>
                    {reindexFailed && (
                      <p className="text-xs text-red-400 mt-1">{t("dashboard.indexing.reindexFailed")}</p>
                    )}
                    <div className="mt-2 space-y-1">
                      {vertexStatus.projects
                        .filter((p) => p.pending_count > 0)
                        .slice(0, 3)
                        .map((p) => (
                          <Link
                            key={p.project_id}
                            to={urls ? urls.project(p.project_id) : "#"}
                            className="block text-xs hover:underline truncate"
                          >
                            {p.project_name} ({p.pending_count})
                          </Link>
                        ))}
                      {vertexStatus.projects.filter((p) => p.pending_count > 0).length > 3 && (
                        <span className="text-xs bento-muted">
                          +{vertexStatus.projects.filter((p) => p.pending_count > 0).length - 3} more
                        </span>
                      )}
                    </div>
                  </>
                ) : vertexStatus && (vertexStatus.total_no_content > 0 || vertexStatus.total_failed > 0) ? (
                  <>
                    <span className="text-5xl font-bold leading-none">
                      {vertexStatus.total_indexed}
                    </span>
                    <p className="text-sm bento-muted mt-2">
                      documents indexed
                    </p>
                    <div className="mt-2 space-y-0.5">
                      {vertexStatus.total_no_content > 0 && (
                        <p className="text-xs bento-muted">
                          {vertexStatus.total_no_content} awaiting transcription
                        </p>
                      )}
                      {vertexStatus.total_failed > 0 && (
                        <p className="text-xs text-red-400 flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3" />
                          {vertexStatus.total_failed} failed
                        </p>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <span className="text-5xl font-bold leading-none">
                      <CheckCircle2 className="h-10 w-10 inline-block" />
                    </span>
                    <p className="text-sm bento-muted mt-2">
                      All documents indexed
                    </p>
                  </>
                )}
              </div>
            </div>
          )}

          {/* ── 6. Pinned Section (surface, spans 2) ── */}
          <div className="bento-card bento-surface lg:col-span-2 md:col-span-2">
            <h2 className="text-lg font-semibold mb-5 flex items-center gap-2">
              <Pin className="h-4 w-4" />
              {t("dashboard.pinnedProjects")}
            </h2>
            {projectsLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="h-14 bg-muted rounded-xl animate-pulse"
                  />
                ))}
              </div>
            ) : pinnedProjects.length > 0 ? (
              <div className="space-y-1">
                {pinnedProjects.map((project) => (
                  <Link
                    key={project.id}
                    to={urls ? urls.project(project.id) : "#"}
                    className="flex items-center justify-between p-3 rounded-xl hover:bg-muted/50 cursor-pointer transition-colors"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="h-11 w-11 rounded-xl bg-nara-orange/15 flex items-center justify-center shrink-0">
                        <FolderKanban className="h-5 w-5 text-nara-orange" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium truncate">{project.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {t("dashboard.docs", {
                            count: project.document_count || 0,
                          })}
                        </p>
                      </div>
                    </div>
                    {project.updated_at && (
                      <span className="text-xs text-muted-foreground shrink-0 ml-4">
                        {new Date(project.updated_at).toLocaleDateString()}
                      </span>
                    )}
                  </Link>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground py-4 text-center">
                No pinned projects yet. Pin a project to see it here.
              </p>
            )}
          </div>

          {/* ── 7. Recent Section (surface) ── */}
          <div className="bento-card bento-surface">
            <h2 className="text-lg font-semibold mb-5 flex items-center gap-2">
              <Clock className="h-4 w-4" />
              {t("dashboard.recentProjects")}
            </h2>
            {projectsLoading ? (
              <div className="space-y-3">
                {[1, 2].map((i) => (
                  <div
                    key={i}
                    className="h-10 bg-muted rounded-lg animate-pulse"
                  />
                ))}
              </div>
            ) : recentProjects.length > 0 ? (
              <div className="space-y-3">
                {recentProjects.map((project) => (
                  <Link
                    key={project.id}
                    to={urls ? urls.project(project.id) : "#"}
                    className="flex items-center gap-3 group"
                  >
                    <span
                      className={`h-2.5 w-2.5 rounded-full shrink-0 ${
                        (project.document_count || 0) > 0
                          ? "bg-green-500"
                          : "bg-yellow-400"
                      }`}
                    />
                    <span className="text-sm truncate group-hover:underline">
                      {project.name}
                    </span>
                    {project.updated_at && (
                      <span className="text-xs text-muted-foreground ml-auto shrink-0">
                        {new Date(project.updated_at).toLocaleDateString()}
                      </span>
                    )}
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-4">
                <FolderKanban className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                <h3 className="font-medium mb-2">
                  {t("dashboard.noProjects")}
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  {t("dashboard.noProjectsMessage")}
                </p>
                {urls && (
                  <Link
                    to={`${urls.projects}?action=new`}
                    className="kii-btn kii-btn-primary kii-btn-sm"
                  >
                    <Plus className="h-4 w-4" />
                    {t("dashboard.newProject")}
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
